/**
 * PreTradingDashboard Component
 *
 * Landing dashboard for Pre-Trading settings with section visibility and ordering.
 * Displays all 5 workflow sections with drag-and-drop reordering (except finalChecks),
 * visibility toggles, and navigation to section-specific settings.
 *
 * Uses manual save pattern - changes are held locally until user clicks Save.
 */

// 1. External dependencies
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  Activity,
  Target,
  Calendar,
  LineChart,
  CheckSquare,
  Crosshair,
  Info,
  Settings,
  AlertTriangle,
  Clock,
  Eye,
  EyeOff,
  type LucideIcon,
} from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  CardFooter,
  Badge,
  Button,
  Switch,
  SortableList,
  DragHandle,
  Skeleton,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { preTradingSettingsService } from '@/services/PreTradingSettingsService';
import { tradingSetupSettingsService } from '@/services/TradingSetupSettingsService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import {
  PRE_TRADING_SECTION_IDS,
  PRE_TRADING_SECTION_META,
  normalizeFixedSections,
  type PreTradingIconName,
} from '@/constants/preTrading';
import type {
  PreTradingSettings,
  PreTradingSectionId,
  PreTradingSectionConfig,
} from '@/types/schema/PreTradingSettings';
import { useLiveSettings } from './hooks/useLiveSettings';
import {
  PersonalPrepFieldsSettingsModal,
  type PersonalPrepField,
} from './PersonalPrepFieldsSettingsModal';
import { HealthMetricsSettingsModal } from './HealthMetricsSettingsModal';
import { ObjectivesConfigModal } from './ObjectivesConfigModal';
import { MarketSnapshotWorkflowConfigModal } from './MarketSnapshotWorkflowConfigModal';
import { EconomicCalendarConfigModal } from './EconomicCalendarConfigModal';
import { FinalChecksSettingsModal } from './FinalChecksSettingsModal';
import type { FinalCheckItem } from './FinalChecksSettingsModal';
import { TransitionSettingsModal } from './TransitionSettingsModal';
import { TradingSetupSettingsModal } from './TradingSetupSettingsModal';
import type { TradingSetupSettings } from '@/types/schema/TradingSetupSettings';
import type { TradingSetupDefaultsUpdate } from '@/types/shared/TradingSetupTypes';

/**
 * Map icon names from constants to Lucide components
 */
const ICON_MAP: Record<PreTradingIconName, LucideIcon> = {
  Heart,
  Activity,
  Target,
  Calendar,
  LineChart,
  Crosshair,
  CheckSquare,
};

/**
 * Local state item for SortableList compatibility
 * Uses 'id' field as required by SortableList
 */
interface SectionRowItem {
  id: PreTradingSectionId;
  visible: boolean;
  order: number;
}

/**
 * Derive SectionRowItem array from sectionConfig or defaults
 * Ensures all sections are always present by merging with defaults
 */
function deriveSectionsFromConfig(
  sectionConfig: PreTradingSectionConfig[] | undefined
): SectionRowItem[] {
  // Build defaults for all sections
  const defaults = PRE_TRADING_SECTION_IDS.map((id, index) => ({
    id,
    visible: true,
    order: index,
  }));

  // If no config or empty, return defaults
  if (!sectionConfig || sectionConfig.length === 0) {
    return defaults;
  }

  // Merge: use config values where they exist, defaults for missing sections
  // This ensures partial configs don't drop sections from the UI
  const merged = defaults.map((defaultItem) => {
    const existing = sectionConfig.find((c) => c.sectionId === defaultItem.id);
    return existing
      ? { id: existing.sectionId, visible: existing.visible, order: existing.order }
      : defaultItem;
  });

  // Normalize: inject missing non-optional sections, force visibility, pin fixed sections
  return normalizeFixedSections(merged);
}

/**
 * Convert SectionRowItem array to PreTradingSectionConfig array for persistence
 * Normalizes order values and enforces non-optional visibility on the write path
 */
function convertToSectionConfig(
  sections: SectionRowItem[]
): PreTradingSectionConfig[] {
  // Separate orderable and fixed sections
  const orderable = sections.filter(
    (s) => PRE_TRADING_SECTION_META[s.id].isOrderable
  );
  const fixed = sections.filter(
    (s) => !PRE_TRADING_SECTION_META[s.id].isOrderable
  );

  // Sort fixed in declaration order for consistent persistence
  const declarationIndex = new Map(PRE_TRADING_SECTION_IDS.map((id, i) => [id as string, i]));
  fixed.sort((a, b) => (declarationIndex.get(a.id) ?? 0) - (declarationIndex.get(b.id) ?? 0));

  // Normalize order values and enforce non-optional visibility
  const normalized: PreTradingSectionConfig[] = [
    ...orderable.map((s, index) => ({
      sectionId: s.id,
      visible: s.visible,
      order: index,
    })),
    ...fixed.map((s, index) => ({
      sectionId: s.id,
      visible: PRE_TRADING_SECTION_META[s.id].isOptional ? s.visible : true,
      order: orderable.length + index,
    })),
  ];

  return normalized;
}

/**
 * Pre-Trading Settings Dashboard
 *
 * Displays workflow sections with visibility toggles and drag-and-drop reordering.
 * Uses manual save pattern - changes require explicit Save action.
 */
export const PreTradingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToastNotification();

  // Local state for sections (working copy)
  const [localSections, setLocalSections] = useState<SectionRowItem[]>([]);

  // Saving state for UI feedback
  const [isSaving, setIsSaving] = useState(false);

  // Baseline for dirty comparison - stores JSON string
  const lastSavedKeyRef = useRef<string>('');

  // External update conflict banner state
  const [dismissedExternalUpdate, setDismissedExternalUpdate] = useState(false);

  // Track previous hasQueuedUpdate for transition detection
  const prevHasQueuedUpdateRef = useRef(false);

  // Modal open state for section config modals
  const [openModal, setOpenModal] = useState<'personalPrep' | 'healthMetrics' | 'objectives' | 'preMarketAnalysis' | 'economicData' | 'finalChecks' | 'transition' | 'tradingSetup' | null>(null);

  // Trading setup settings loaded on-demand when gear icon is clicked
  const [tradingSetupSettings, setTradingSetupSettings] = useState<TradingSetupSettings | null>(null);

  // Handle external updates (show toast)
  const handleExternalUpdate = useCallback(() => {
    showToast({
      title: 'Settings Updated',
      description: 'Settings updated from another device',
      color: 'success',
    });
  }, [showToast]);

  // Memoize serialized local sections for dirty detection
  const localSectionsKey = useMemo(
    () => JSON.stringify(localSections),
    [localSections]
  );

  // Compute dirty state
  const isDirty = localSectionsKey !== lastSavedKeyRef.current;

  // Live settings with dirty-state awareness
  const { settings, setSettings, isLoading, error, hasQueuedUpdate } =
    useLiveSettings({
      table: 'pre_trading_settings',
      fetchSettings: () => preTradingSettingsService.getCurrentSettings(),
      isDirty,
      onExternalUpdate: handleExternalUpdate,
    });

  // Show conflict banner when external update available while editing
  const showConflictBanner = hasQueuedUpdate && isDirty && !dismissedExternalUpdate;

  // Reset dismissedExternalUpdate when new external update arrives
  useEffect(() => {
    const wasQueued = prevHasQueuedUpdateRef.current;
    const isQueued = hasQueuedUpdate;
    prevHasQueuedUpdateRef.current = hasQueuedUpdate;

    // Transition false→true means new external update arrived
    if (!wasQueued && isQueued) {
      setDismissedExternalUpdate(false);
    }
  }, [hasQueuedUpdate]);

  // Memoize settings sectionConfig key for sync detection
  const settingsConfigKey = useMemo(
    () => JSON.stringify(settings?.sectionConfig),
    [settings?.sectionConfig]
  );

  // Initialize local state from settings (or defaults)
  // Also handles sync from props when not dirty
  useEffect(() => {
    if (!settings) return;

    const derived = deriveSectionsFromConfig(settings.sectionConfig);
    const derivedKey = JSON.stringify(derived);

    // First load: initialize both local state and baseline
    if (lastSavedKeyRef.current === '') {
      setLocalSections(derived);
      lastSavedKeyRef.current = derivedKey;
      return;
    }

    // External update while not dirty: sync local state
    if (!isDirty && derivedKey !== lastSavedKeyRef.current) {
      setLocalSections(derived);
      lastSavedKeyRef.current = derivedKey;
    }
  }, [settings, settingsConfigKey, isDirty]);

  // Error toast on load failure
  useEffect(() => {
    if (error) {
      showToast({
        title: 'Error Loading Settings',
        description: error,
        color: 'danger',
      });
    }
  }, [error, showToast]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Toggle visibility for a section
   */
  const handleVisibilityToggle = useCallback((sectionId: PreTradingSectionId) => {
    setLocalSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, visible: !s.visible } : s))
    );
  }, []);

  /**
   * Handle reorder via drag-and-drop (only orderable sections)
   */
  const handleReorder = useCallback((reorderedItems: SectionRowItem[]) => {
    setLocalSections((prev) => {
      // Keep fixed sections (finalChecks) at the end
      const fixed = prev.filter((s) => !PRE_TRADING_SECTION_META[s.id].isOrderable);
      return [...reorderedItems, ...fixed];
    });
  }, []);

  /**
   * Handle settings button click - open modal for supported sections, navigate for others
   */
  const handleSettingsClick = useCallback(
    async (sectionId: PreTradingSectionId, route: string | null) => {
      // Guard: don't allow opening modal while section list is dirty or loading
      if (isDirty || isLoading || !settings) return;

      // Trading setup uses async on-demand settings load
      if (sectionId === 'tradingSetup') {
        try {
          const tsSettings = await tradingSetupSettingsService.getCurrentSettings();
          setTradingSetupSettings(tsSettings);
          setOpenModal('tradingSetup');
        } catch (err) {
          console.error('Failed to load trading setup settings:', err);
          showToast({
            title: 'Failed to Load Settings',
            description: 'Could not load trading setup settings. Please try again.',
            color: 'danger',
          });
        }
        return;
      }

      // Open modal for personalPrep, healthMetrics, objectives, preMarketAnalysis, economicData, and finalChecks sections
      if (sectionId === 'personalPrep' || sectionId === 'healthMetrics' || sectionId === 'objectives' || sectionId === 'preMarketAnalysis' || sectionId === 'economicData' || sectionId === 'finalChecks') {
        setOpenModal(sectionId);
        return;
      }

      // Navigate to route for other sections
      if (route) {
        navigate(route);
      }
    },
    [isDirty, isLoading, settings, navigate, showToast]
  );

  /**
   * Save changes - normalize order, persist, sync, update baseline
   */
  const handleSave = useCallback(async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      // Convert to normalized sectionConfig
      const sectionConfig = convertToSectionConfig(localSections);

      // Build updated settings
      const updated: PreTradingSettings = {
        ...settings,
        sectionConfig,
      };

      // Persist to IndexedDB
      await preTradingSettingsService.update(settings.id, updated);

      // Update hook state to match persisted data
      setSettings(updated);

      // Notify other tabs of the change
      syncCoordinatorService.notifyChange('pre_trading_settings', settings.id);

      // Update local state and baseline to match normalized values
      // This ensures isDirty becomes false after save
      const normalizedSections = deriveSectionsFromConfig(sectionConfig);
      setLocalSections(normalizedSections);
      lastSavedKeyRef.current = JSON.stringify(normalizedSections);

      // Reset conflict banner state
      setDismissedExternalUpdate(false);

      showToast({
        title: 'Settings Saved',
        description: 'Section visibility and order updated',
        color: 'success',
      });
    } catch (err) {
      console.error('Failed to save section config:', err);
      showToast({
        title: 'Save Failed',
        description: 'Your changes are preserved. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [settings, localSections, setSettings, showToast]);

  /**
   * Discard changes - reset to current settings and update baseline
   * Updates baseline to handle case where external update arrived while editing
   */
  const handleDiscard = useCallback(() => {
    if (!settings) return;

    const baseline = deriveSectionsFromConfig(settings.sectionConfig);
    setLocalSections(baseline);
    // Update baseline to match current settings (handles external updates during edit)
    lastSavedKeyRef.current = JSON.stringify(baseline);
    // Reset conflict banner state
    setDismissedExternalUpdate(false);
  }, [settings]);

  /**
   * Accept external changes - reset local to settings, clear dirty
   */
  const handleAcceptExternalChanges = useCallback(() => {
    if (!settings) return;

    const derived = deriveSectionsFromConfig(settings.sectionConfig);
    setLocalSections(derived);
    lastSavedKeyRef.current = JSON.stringify(derived);
    setDismissedExternalUpdate(false);
  }, [settings]);

  /**
   * Keep editing - dismiss the conflict banner
   */
  const handleKeepEditing = useCallback(() => {
    setDismissedExternalUpdate(true);
  }, []);

  /**
   * Close any open modal
   */
  const handleCloseModal = useCallback(() => {
    setOpenModal(null);
  }, []);

  /**
   * Save personal prep fields from modal
   * Uses freshest settings state to preserve external changes to other fields
   */
  const handleSavePersonalPrepFields = useCallback(
    async (updatedFields: PersonalPrepField[]) => {
      if (!settings) return;

      // Build updated settings with new personalPrepFields (using freshest settings)
      const updated: PreTradingSettings = {
        ...settings,
        personalPrepFields: updatedFields,
      };

      // Persist to IndexedDB
      await preTradingSettingsService.update(settings.id, updated);

      // Update hook state
      setSettings(updated);

      // Notify other tabs
      syncCoordinatorService.notifyChange('pre_trading_settings', settings.id);

      // Show success toast
      showToast({
        title: 'Personal Prep Fields Saved',
        description: 'Your personal preparation fields have been updated',
        color: 'success',
      });

      // Close modal on success
      setOpenModal(null);
    },
    [settings, setSettings, showToast]
  );

  /**
   * Save health metrics from modal
   * Uses freshest settings state to preserve external changes to other fields
   */
  const handleSaveHealthMetrics = useCallback(
    async (
      source: 'apple' | 'whoop' | 'oura' | 'garmin' | 'custom',
      metrics: Array<{ id: string; name: string; min: number; max: number; unit?: string }>
    ) => {
      if (!settings) return;

      // Build updated settings with new healthMetrics (using freshest settings)
      const updated: PreTradingSettings = {
        ...settings,
        healthMetrics: {
          source,
          metrics,
        },
      };

      // Persist to IndexedDB
      await preTradingSettingsService.update(settings.id, updated);

      // Update hook state
      setSettings(updated);

      // Notify other tabs
      syncCoordinatorService.notifyChange('pre_trading_settings', settings.id);

      // Show success toast
      showToast({
        title: 'Health Metrics Saved',
        description: 'Your health metrics configuration has been updated',
        color: 'success',
      });

      // Close modal on success
      setOpenModal(null);
    },
    [settings, setSettings, showToast]
  );

  /**
   * Save objectives config from modal
   * Uses freshest settings state to preserve external changes to other fields
   */
  const handleSaveObjectivesConfig = useCallback(
    async (maxObjectives: number, pinObjectivesToLive: boolean) => {
      if (!settings) return;

      // Build updated settings with new objectives config (using freshest settings)
      const updated: PreTradingSettings = {
        ...settings,
        maxObjectives,
        pinObjectivesToLive,
      };

      // Persist to IndexedDB
      await preTradingSettingsService.update(settings.id, updated);

      // Update hook state
      setSettings(updated);

      // Notify other tabs
      syncCoordinatorService.notifyChange('pre_trading_settings', settings.id);

      // Show success toast
      showToast({
        title: 'Objectives Settings Saved',
        description: 'Your objectives configuration has been updated',
        color: 'success',
      });

      // Close modal on success
      setOpenModal(null);
    },
    [settings, setSettings, showToast]
  );

  /**
   * Save final check items from modal
   * Uses freshest settings state to preserve external changes to other fields
   */
  const handleSaveFinalCheckItems = useCallback(
    async (updatedItems: FinalCheckItem[]) => {
      if (!settings) return;

      // Build updated settings with new finalCheckItems (using freshest settings)
      const updated: PreTradingSettings = {
        ...settings,
        finalCheckItems: updatedItems,
      };

      // Persist to IndexedDB
      await preTradingSettingsService.update(settings.id, updated);

      // Update hook state
      setSettings(updated);

      // Notify other tabs
      syncCoordinatorService.notifyChange('pre_trading_settings', settings.id);

      // Show success toast
      showToast({
        title: 'Final Checks Saved',
        description: 'Your final check items have been updated',
        color: 'success',
      });

      // Close modal on success
      setOpenModal(null);
    },
    [settings, setSettings, showToast]
  );

  /**
   * Save transition settings from modal (duration, autoAdvance, quotes only)
   * Uses freshest settings state to preserve external changes to other fields
   */
  const handleSaveTransitionSettings = useCallback(
    async (transitionSettings: {
      transitionDuration: number;
      autoAdvance: boolean;
      transitionQuotes: import('@/types/schema/PreTradingSettings').TransitionQuote[];
    }) => {
      if (!settings) return;

      // Build updated settings with new transition config (using freshest settings)
      // Note: enableTransitionScreen is managed by the card toggle, not the modal
      const updated: PreTradingSettings = {
        ...settings,
        transitionDuration: transitionSettings.transitionDuration,
        autoAdvance: transitionSettings.autoAdvance,
        transitionQuotes: transitionSettings.transitionQuotes,
      };

      // Persist to IndexedDB
      await preTradingSettingsService.update(settings.id, updated);

      // Update hook state
      setSettings(updated);

      // Notify other tabs
      syncCoordinatorService.notifyChange('pre_trading_settings', settings.id);

      // Show success toast
      showToast({
        title: 'Transition Settings Saved',
        description: 'Your transition screen settings have been updated',
        color: 'success',
      });

      // Close modal on success
      setOpenModal(null);
    },
    [settings, setSettings, showToast]
  );

  /**
   * Toggle transition screen enable/disable from card
   * Immediate persistence (not part of the manual save flow for section order)
   */
  const handleTransitionEnableToggle = useCallback(
    async (isEnabled: boolean) => {
      if (!settings) return;

      try {
        // Build updated settings with new enable state
        const updated: PreTradingSettings = {
          ...settings,
          enableTransitionScreen: isEnabled,
        };

        // Persist to IndexedDB
        await preTradingSettingsService.update(settings.id, updated);

        // Update hook state
        setSettings(updated);

        // Notify other tabs
        syncCoordinatorService.notifyChange('pre_trading_settings', settings.id);

        // Show success toast
        showToast({
          title: isEnabled ? 'Transition Screen Enabled' : 'Transition Screen Disabled',
          description: isEnabled
            ? 'The transition screen will appear before live trading'
            : 'The transition screen has been turned off',
          color: 'success',
        });
      } catch (err) {
        console.error('Failed to toggle transition screen:', err);
        showToast({
          title: 'Toggle Failed',
          description: 'Could not update transition screen setting. Please try again.',
          color: 'danger',
        });
      }
    },
    [settings, setSettings, showToast]
  );

  /**
   * Save trading setup settings from modal.
   * Errors propagate to the modal's handleSave catch block (modal owns error UI).
   */
  const handleSaveTradingSetup = useCallback(
    async (output: TradingSetupDefaultsUpdate) => {
      const settings = await tradingSetupSettingsService.getCurrentSettings();
      await tradingSetupSettingsService.saveDefaults(settings.id, output);

      showToast({
        title: 'Trading Setup Defaults Saved',
        description: 'Your default trading setup preferences have been updated',
        color: 'success',
      });
    },
    [showToast]
  );

  // ============================================================================
  // Section Row Renderers
  // ============================================================================

  /**
   * Render a single orderable section row (with drag handle)
   */
  const renderSectionRow = useCallback(
    (item: SectionRowItem, dragHandleProps: DragHandleProps) => {
      const meta = PRE_TRADING_SECTION_META[item.id];
      const IconComponent = ICON_MAP[meta.icon];
      const isHidden = !item.visible;
      // Disable settings button when: hidden, coming soon, list is dirty, or loading
      const settingsDisabled = isHidden || meta.isComingSoon || isDirty || isLoading;

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={`
            ${dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
            ${isHidden ? 'opacity-50' : ''}
          `}
        >
          <CardBody className="p-3">
            {/* Mobile Layout */}
            <div className="md:hidden flex items-center gap-2">
              <DragHandle dragHandleProps={dragHandleProps} />
              <div className="text-primary shrink-0">
                <IconComponent size={20} />
              </div>
              <div className="flex-1 min-w-0">
                {meta.isComingSoon ? (
                  <Badge content="Soon" size="sm" placement="top-left" classNames={{ badge: "bg-default-100" }}>
                    <span className="font-medium truncate">{meta.title}</span>
                  </Badge>
                ) : (
                  <span className="font-medium truncate">{meta.title}</span>
                )}
              </div>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                isDisabled={settingsDisabled}
                onPress={() => handleSettingsClick(item.id, meta.settingsRoute)}
                aria-label={`Configure ${meta.title}`}
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Switch
                size="sm"
                isSelected={item.visible}
                onValueChange={() => handleVisibilityToggle(item.id)}
                aria-label={`Toggle ${meta.title} visibility`}
                thumbIcon={({ isSelected, className }) =>
                  isSelected ? (
                    <Eye className={`${className} text-primary`} />
                  ) : (
                    <EyeOff className={`${className} text-primary`} />
                  )
                }
              />
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center gap-4">
              <DragHandle dragHandleProps={dragHandleProps} />

              <div className="text-primary shrink-0 w-6">
                <IconComponent size={24} />
              </div>

              <div className="flex-1 min-w-0">
                {meta.isComingSoon ? (
                  <Badge content="Soon" size="sm" placement="top-left" classNames={{ badge: "bg-default-100" }}>
                    <span className="font-medium">{meta.title}</span>
                  </Badge>
                ) : (
                  <span className="font-medium">{meta.title}</span>
                )}
                <p className="text-sm text-default-500 truncate">
                  {meta.description}
                </p>
              </div>

              <div className="w-10 flex justify-center">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  isDisabled={settingsDisabled}
                  onPress={() => handleSettingsClick(item.id, meta.settingsRoute)}
                  aria-label={`Configure ${meta.title}`}
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </div>

              <div className="w-16 flex justify-center">
                <Switch
                  size="sm"
                  isSelected={item.visible}
                  onValueChange={() => handleVisibilityToggle(item.id)}
                  aria-label={`Toggle ${meta.title} visibility`}
                  thumbIcon={({ isSelected, className }) =>
                    isSelected ? (
                      <Eye className={`${className} text-primary`} />
                    ) : (
                      <EyeOff className={`${className} text-primary`} />
                    )
                  }
                />
              </div>
            </div>
          </CardBody>
        </Card>
      );
    },
    [handleVisibilityToggle, handleSettingsClick, isDirty, isLoading]
  );

  /**
   * Render the fixed section row (finalChecks - no drag handle)
   */
  const renderFixedSectionRow = useCallback(
    (item: SectionRowItem) => {
      const meta = PRE_TRADING_SECTION_META[item.id];
      const IconComponent = ICON_MAP[meta.icon];
      const isHidden = !item.visible;
      // Disable settings button when: hidden, coming soon, list is dirty, or loading
      const settingsDisabled = isHidden || meta.isComingSoon || isDirty || isLoading;

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={isHidden ? 'opacity-50' : ''}
        >
          <CardBody className="p-3">
            {/* Mobile Layout */}
            <div className="md:hidden flex items-center gap-2">
              <div className="w-11 shrink-0" /> {/* Spacer to align with orderable rows */}
              <div className="text-primary shrink-0">
                <IconComponent size={20} />
              </div>
              <div className="flex-1 min-w-0">
                {meta.isComingSoon ? (
                  <Badge content="Soon" size="sm" placement="top-left" classNames={{ badge: "bg-default-100" }}>
                    <span className="font-medium truncate">{meta.title}</span>
                  </Badge>
                ) : (
                  <span className="font-medium truncate">{meta.title}</span>
                )}
              </div>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                isDisabled={settingsDisabled}
                onPress={() => handleSettingsClick(item.id, meta.settingsRoute)}
                aria-label={`Configure ${meta.title}`}
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Switch
                size="sm"
                isSelected={item.visible}
                onValueChange={() => handleVisibilityToggle(item.id)}
                aria-label={`Toggle ${meta.title} visibility`}
                thumbIcon={({ isSelected, className }) =>
                  isSelected ? (
                    <Eye className={`${className} text-primary`} />
                  ) : (
                    <EyeOff className={`${className} text-primary`} />
                  )
                }
              />
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center gap-4">
              <div className="w-11 shrink-0" /> {/* Spacer to align with orderable rows */}

              <div className="text-primary shrink-0 w-6">
                <IconComponent size={24} />
              </div>

              <div className="flex-1 min-w-0">
                {meta.isComingSoon ? (
                  <Badge content="Soon" size="sm" placement="top-left" classNames={{ badge: "bg-default-100" }}>
                    <span className="font-medium">{meta.title}</span>
                  </Badge>
                ) : (
                  <span className="font-medium">{meta.title}</span>
                )}
                <p className="text-sm text-default-500 truncate">
                  {meta.description}
                </p>
              </div>

              <div className="w-10 flex justify-center">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  isDisabled={settingsDisabled}
                  onPress={() => handleSettingsClick(item.id, meta.settingsRoute)}
                  aria-label={`Configure ${meta.title}`}
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </div>

              <div className="w-16 flex justify-center">
                <Switch
                  size="sm"
                  isSelected={item.visible}
                  onValueChange={() => handleVisibilityToggle(item.id)}
                  aria-label={`Toggle ${meta.title} visibility`}
                  thumbIcon={({ isSelected, className }) =>
                    isSelected ? (
                      <Eye className={`${className} text-primary`} />
                    ) : (
                      <EyeOff className={`${className} text-primary`} />
                    )
                  }
                />
              </div>
            </div>
          </CardBody>
        </Card>
      );
    },
    [handleVisibilityToggle, handleSettingsClick, isDirty, isLoading]
  );

  /**
   * Render a non-optional fixed section row (no drag handle, Info indicator instead of Switch)
   */
  const renderNonOptionalSectionRow = useCallback(
    (item: SectionRowItem) => {
      const meta = PRE_TRADING_SECTION_META[item.id];
      const IconComponent = ICON_MAP[meta.icon];
      // Non-optional sections are never disabled for settings (always visible)
      const settingsDisabled = isDirty || isLoading;

      return (
        <Card shadow="sm" radius="sm">
          <CardBody className="p-3">
            {/* Mobile Layout */}
            <div className="md:hidden flex items-center gap-2">
              <div className="w-11 shrink-0" /> {/* Spacer to align with orderable rows */}
              <div className="text-primary shrink-0">
                <IconComponent size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium truncate">{meta.title}</span>
              </div>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                isDisabled={settingsDisabled}
                onPress={() => handleSettingsClick(item.id, meta.settingsRoute)}
                aria-label={`Configure ${meta.title}`}
              >
                <Settings className="w-4 h-4" />
              </Button>
              <div className="w-10 flex justify-center shrink-0">
                <Popover placement="top">
                  <PopoverTrigger>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label={`This section is always visible — ${meta.description}`}
                    >
                      <Info size={20} className="text-primary" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <p className="text-sm p-2">Required each session to capture trading intent</p>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center gap-4">
              <div className="w-11 shrink-0" /> {/* Spacer to align with orderable rows */}

              <div className="text-primary shrink-0 w-6">
                <IconComponent size={24} />
              </div>

              <div className="flex-1 min-w-0">
                <span className="font-medium">{meta.title}</span>
                <p className="text-sm text-default-500 truncate">
                  {meta.description}
                </p>
              </div>

              <div className="w-10 flex justify-center">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  isDisabled={settingsDisabled}
                  onPress={() => handleSettingsClick(item.id, meta.settingsRoute)}
                  aria-label={`Configure ${meta.title}`}
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </div>

              <div className="w-16 flex justify-center">
                <Tooltip content="Required each session to capture trading intent">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label={`This section is always visible — ${meta.description}`}
                  >
                    <Info size={18} className="text-primary" />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </CardBody>
        </Card>
      );
    },
    [handleSettingsClick, isDirty, isLoading]
  );

  // ============================================================================
  // Render
  // ============================================================================

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  // Error state (no settings loaded)
  if (!settings) {
    return (
      <div className="space-y-6">
        <p className="text-danger">
          Failed to load settings. Please refresh the page.
        </p>
      </div>
    );
  }

  // Split sections into orderable and fixed
  const orderableSections = localSections.filter(
    (s) => PRE_TRADING_SECTION_META[s.id].isOrderable
  );
  const fixedSections = localSections.filter(
    (s) => !PRE_TRADING_SECTION_META[s.id].isOrderable
  );
  // Further split fixed: non-optional (Info indicator) vs optional (Switch toggle)
  const nonOptionalFixed = fixedSections.filter(
    (s) => !PRE_TRADING_SECTION_META[s.id].isOptional
  );
  const optionalFixed = fixedSections.filter(
    (s) => PRE_TRADING_SECTION_META[s.id].isOptional
  );

  return (
    <div className="space-y-2">
      {/* Sections List */}
      <Card shadow='none' radius='none'>
        <CardBody className="space-y-2 px-1">

          {/* Orderable Sections (SortableList) */}
          <SortableList
            items={orderableSections}
            onReorder={handleReorder}
            renderItem={renderSectionRow}
            className="space-y-2"
          />

          {/* Non-optional fixed sections (Info indicator, no visibility toggle) */}
          {nonOptionalFixed.map((section) => (
            <div key={section.id}>{renderNonOptionalSectionRow(section)}</div>
          ))}

          {/* Optional fixed sections (Switch toggle, no drag handle) */}
          {optionalFixed.map((section) => (
            <div key={section.id}>{renderFixedSectionRow(section)}</div>
          ))}

          {/* Transition Screen Row - styled to match section cards */}
          <Card
            shadow="sm"
            radius="sm"
            className={!settings?.enableTransitionScreen ? 'opacity-50' : ''}
          >
            <CardBody className="p-3">
              {/* Mobile Layout */}
              <div className="md:hidden flex items-center gap-2">
                <div className="w-11 shrink-0" /> {/* Spacer to align with section rows */}
                <div className="text-primary shrink-0">
                  <Clock size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium truncate">Transition Screen</span>
                </div>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  isDisabled={isDirty || isLoading || !settings || !settings.enableTransitionScreen}
                  onPress={() => {
                    if (isDirty || isLoading || !settings || !settings.enableTransitionScreen) return;
                    setOpenModal('transition');
                  }}
                  aria-label="Configure transition screen"
                >
                  <Settings className="w-4 h-4" />
                </Button>
                <Switch
                  size="sm"
                  isSelected={settings?.enableTransitionScreen ?? false}
                  isDisabled={isDirty || isLoading || !settings}
                  onValueChange={handleTransitionEnableToggle}
                  aria-label="Toggle transition screen"
                  thumbIcon={({ isSelected, className }) =>
                    isSelected ? (
                      <Eye className={`${className} text-primary`} />
                    ) : (
                      <EyeOff className={`${className} text-primary`} />
                    )
                  }
                />
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:flex items-center gap-4">
                <div className="w-11 shrink-0" /> {/* Spacer to align with section rows */}

                <div className="text-primary shrink-0 w-6">
                  <Clock size={24} />
                </div>

                <div className="flex-1 min-w-0">
                  <span className="font-medium">Transition Screen</span>
                  <p className="text-sm text-default-500 truncate">
                    Mental transition with quotes before live trading
                  </p>
                </div>

                <div className="w-10 flex justify-center">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    isDisabled={isDirty || isLoading || !settings || !settings.enableTransitionScreen}
                    onPress={() => {
                      if (isDirty || isLoading || !settings || !settings.enableTransitionScreen) return;
                      setOpenModal('transition');
                    }}
                    aria-label="Configure transition screen"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>

                <div className="w-16 flex justify-center">
                  <Switch
                    size="sm"
                    isSelected={settings?.enableTransitionScreen ?? false}
                    isDisabled={isDirty || isLoading || !settings}
                    onValueChange={handleTransitionEnableToggle}
                    aria-label="Toggle transition screen"
                    thumbIcon={({ isSelected, className }) =>
                      isSelected ? (
                        <Eye className={`${className} text-primary`} />
                      ) : (
                        <EyeOff className={`${className} text-primary`} />
                      )
                    }
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* External Update Conflict Banner */}
          {showConflictBanner && (
            <div className="rounded-lg bg-warning-50 border border-warning-200 p-4 mt-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0" />
                  <span className="text-sm text-warning-700">
                    Changes available from another tab
                  </span>
                </div>
                <div className="flex flex-col gap-2 md:flex-row">
                  <Button size="sm" variant="flat" onPress={handleKeepEditing}>
                    Keep editing
                  </Button>
                  <Button
                    size="sm"
                    color="warning"
                    onPress={handleAcceptExternalChanges}
                  >
                    Accept changes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardBody>

        {/* Footer with Save/Discard - only visible when dirty */}
        {isDirty && (
          <CardFooter className="flex items-center justify-between gap-4 border-t border-default-200 px-0">
            <span className="text-sm text-warning-600">Unsaved changes</span>
            <div className="flex gap-2">
              <Button variant="flat" onPress={handleDiscard} isDisabled={isSaving}>
                Discard
              </Button>
              <Button
                color="primary"
                onPress={handleSave}
                isDisabled={isSaving}
                isLoading={isSaving}
              >
                Save
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Personal Prep Fields Settings Modal */}
      <PersonalPrepFieldsSettingsModal
        isOpen={openModal === 'personalPrep'}
        onClose={handleCloseModal}
        onSave={handleSavePersonalPrepFields}
        initialFields={settings?.personalPrepFields ?? []}
      />

      {/* Health Metrics Settings Modal */}
      <HealthMetricsSettingsModal
        isOpen={openModal === 'healthMetrics'}
        onClose={handleCloseModal}
        onSave={handleSaveHealthMetrics}
        initialSource={settings?.healthMetrics?.source ?? 'custom'}
        initialMetrics={settings?.healthMetrics?.metrics ?? []}
      />

      {/* Objectives Config Modal */}
      <ObjectivesConfigModal
        isOpen={openModal === 'objectives'}
        onClose={handleCloseModal}
        onSave={handleSaveObjectivesConfig}
        initialMaxObjectives={settings?.maxObjectives ?? 3}
        initialPinObjectivesToLive={settings?.pinObjectivesToLive ?? false}
      />

      {/* Market Snapshot Settings Modal */}
      <MarketSnapshotWorkflowConfigModal
        isOpen={openModal === 'preMarketAnalysis'}
        onClose={handleCloseModal}
        onSaveSuccess={handleCloseModal}
      />

      {/* Economic Calendar Config Modal */}
      <EconomicCalendarConfigModal
        isOpen={openModal === 'economicData'}
        onClose={handleCloseModal}
        onSaveSuccess={handleCloseModal}
      />

      {/* Final Checks Settings Modal */}
      <FinalChecksSettingsModal
        isOpen={openModal === 'finalChecks'}
        onClose={handleCloseModal}
        onSave={handleSaveFinalCheckItems}
        initialItems={settings?.finalCheckItems ?? []}
      />

      {/* Transition Settings Modal */}
      <TransitionSettingsModal
        isOpen={openModal === 'transition'}
        onClose={handleCloseModal}
        onSave={handleSaveTransitionSettings}
        initialTransitionDuration={settings?.transitionDuration ?? 5}
        initialAutoAdvance={settings?.autoAdvance ?? false}
        initialQuotes={settings?.transitionQuotes ?? []}
      />

      {/* Trading Setup Settings Modal */}
      <TradingSetupSettingsModal
        isOpen={openModal === 'tradingSetup'}
        onClose={handleCloseModal}
        onSave={handleSaveTradingSetup}
        initialPairs={tradingSetupSettings?.defaultStrategyPlaybookPairs ?? []}
        initialAccountId={tradingSetupSettings?.defaultAccountId}
        initialFocusInstrumentIds={tradingSetupSettings?.defaultFocusInstrumentIds ?? []}
      />
    </div>
  );
};
